import { HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { FeatureFlagService } from "@dcc/shared/feature-flags/data-access";
import { setError, setLoaded, setLoading, withCallState } from "@dcc/shared/state";
import { notificationEvents } from "@dcc/vestas-online/alerts/domain";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withMethods } from "@ngrx/signals";
import { setAllEntities, updateEntity, withEntities } from "@ngrx/signals/entities";
import { injectDispatch } from "@ngrx/signals/events";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { concatMap, iif, pipe, switchMap, tap } from "rxjs";
import { BetaFeature } from "../models/beta-feature.model";
import { BetaFeaturesApiService } from "../services/beta-features-api.service";

export const BetaFeaturesApiStore = signalStore(
    { providedIn: "root" },
    withCallState(),
    withEntities<BetaFeature>(),
    withMethods(
        (
            store,
            api = inject(BetaFeaturesApiService),
            notifications = injectDispatch(notificationEvents),
            features = inject(FeatureFlagService),
            router = inject(Router)
        ) => ({
            load: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, setLoading())),
                    switchMap(() =>
                        api.getAll().pipe(
                            tapResponse({
                                next: (flags) => {
                                    patchState(store, setLoaded(), setAllEntities(flags, { selectId: (flag) => flag.key }));
                                },
                                error: (error: HttpErrorResponse) => patchState(store, setError(error.message))
                            })
                        )
                    )
                )
            ),
            toggleFeature: rxMethod<BetaFeature>(
                concatMap((feature) => {
                    return iif(() => feature.enabled, api.disable(feature), api.enable(feature)).pipe(
                        tapResponse({
                            next: () => {
                                const enabled = !feature.enabled;

                                patchState(
                                    store,
                                    updateEntity(
                                        {
                                            id: feature.key,
                                            changes: { enabled }
                                        },
                                        { selectId: (flag) => flag.key }
                                    )
                                );

                                features.reload();
                                router.navigate(["/app/jumpboard"]);
                            },
                            error: () => notifications.translatedToastShown({ message: "betaFeatureToggleError", type: "error" })
                        })
                    );
                })
            )
        })
    )
);
